<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="modelReg">
    <div class="modelReg__grid">
      <div class="modelReg__title">
        <h3 class="text-3xl font-bold">{{ $t('modal.auth_modal_state.registration.title') }}</h3>
      </div>

      <div v-if="loader" class="modelReg__alertLoading"></div>
      <div v-if="errorMessage" class="modelReg__alertError">{{ errorMessage }}</div>
      <div v-if="successMessage" class="modelReg__alertSuccess">{{ successMessage }}</div>

      <!-- ✅ Уведомление о реферальной программе -->
      <UAlert v-if="hasPendingToken" color="purple" variant="soft" icon="i-heroicons-gift" class="mb-4">
        <template #title>Реферальная программа</template>
        <template #description>После регистрации вам будет начислен бонус за приглашение друга!</template>
      </UAlert>

      <!-- Кнопки способов регистрации выглядят обычными, а согласие проверяется
           в момент нажатия (см. useAuthConsentGate): серая кнопка не объясняет,
           чего от человека хотят, а CSS-гейт обходился с клавиатуры. -->
      <div class="modelReg__methods">
      <!-- Telegram авторизация -->
      <div v-if="isTelegramAuthEnabled" class="grid gap-4">
        <UButton
          color="blue"
          size="xl"
          block
          class="h-12 dark:text-white"
          :ui="{ rounded: 'rounded-xl' }"
          :loading="telegramLoading"
          @click="handleTelegramAuth"
        >
          <template #leading>
            <UIcon name="i-logos-telegram" class="w-6 h-6" />
          </template>
          {{ telegramLoading ? 'Ожидание авторизации...' : 'Войти через Telegram' }}
        </UButton>

        <!-- Модальное окно с кнопкой для Safari -->
        <div
          v-if="showTelegramLinkModal"
          class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
          @click="closeTelegramLinkModal"
        >
          <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 shadow-xl" @click.stop>
            <h3 class="text-lg font-semibold mb-4">Открыть Telegram</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Нажмите на кнопку ниже, чтобы продолжить авторизацию в Telegram
            </p>
            <div class="flex gap-3">
              <UButton color="blue" size="lg" block @click="handleOpenTelegramLink">
                <template #leading>
                  <UIcon name="i-logos-telegram" size="lg" />
                </template>
                Открыть Telegram
              </UButton>
            </div>
            <UButton color="gray" variant="ghost" size="sm" block class="mt-3" @click="closeTelegramLinkModal">
              Отмена
            </UButton>
          </div>
        </div>
      </div>

      <!-- MAX авторизация -->
      <div v-if="isMaxAuthEnabled" class="grid gap-4 mt-4">
        <UButton
          size="xl"
          block
          class="h-12 border-0 text-white dark:text-white bg-[linear-gradient(90deg,_#44CCFF_0%,_#5533EE_66%,_#9933DD_100%)] hover:bg-[linear-gradient(90deg,_#3db8e6_0%,_#4c2ed6_66%,_#8a2fc4_100%)]"
          :ui="{ rounded: 'rounded-xl' }"
          :loading="maxLoading"
          @click="handleMaxAuth"
        >
          <template #leading>
            <IconTheMax class="w-6 h-6 text-white" />
          </template>
          {{ maxLoading ? 'Ожидание авторизации...' : 'Войти через MAX' }}
        </UButton>

        <!-- Модальное окно с кнопкой для Safari (MAX) -->
        <div
          v-if="showMaxLinkModal"
          class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
          @click="closeMaxLinkModal"
        >
          <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 shadow-xl" @click.stop>
            <h3 class="text-lg font-semibold mb-4">Подтвердите вход в MAX</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Нажмите кнопку ниже, перейдите в MAX и подтвердите авторизацию
            </p>
            <div class="flex gap-3">
              <UButton
                size="lg"
                block
                class="border-0 text-white bg-[linear-gradient(90deg,_#44CCFF_0%,_#5533EE_66%,_#9933DD_100%)] hover:bg-[linear-gradient(90deg,_#3db8e6_0%,_#4c2ed6_66%,_#8a2fc4_100%)]"
                @click="handleOpenMaxLink"
              >
                <template #leading>
                  <IconTheMax class="w-6 h-6 text-white" />
                </template>
                Открыть MAX
              </UButton>
            </div>
            <UButton color="gray" variant="ghost" size="sm" block class="mt-3" @click="closeMaxLinkModal">
              Отмена
            </UButton>
          </div>
        </div>
      </div>

      <!-- VK ID авторизация: на нативных платформах — кнопка (нативный VKID SDK),
           в вебе — виджет OneTap, который SDK рендерит в контейнер. -->
      <div v-if="showVk" class="grid gap-4 mt-4">
        <UButton
          v-if="isVkNative"
          block
          size="xl"
          color="blue"
          class="h-12 dark:text-white"
          :ui="{ rounded: 'rounded-xl' }"
          icon="i-simple-icons-vk"
          :loading="isVkLoading"
          label="Войти через VK ID"
          @click="handleVkNativeAuth"
        />
        <!-- VK ID рисует OneTap сам внутри контейнера, и до его клика наш обработчик
             не достаёт. Пока согласия нет — накрываем прозрачным слоем, который
             перехватывает нажатие и подсвечивает галочку. -->
        <div v-else class="relative w-full">
          <div ref="vkContainer" class="w-full"></div>
          <button
            v-if="!isConsentComplete"
            type="button"
            class="absolute inset-0 z-10 cursor-pointer"
            aria-label="Войти с VK ID"
            @click="requireConsent"
          ></button>
        </div>
      </div>

      <!-- Яндекс ID авторизация (переиспользуемый компонент) -->
      <div class="relative mt-4">
        <YandexAuthButton size="xl" />
        <button
          v-if="!isConsentComplete"
          type="button"
          class="absolute inset-0 z-10 cursor-pointer"
          aria-label="Войти через Яндекс ID"
          @click="requireConsent"
        ></button>
      </div>
      </div>

      <span v-if="(isTelegramAuthEnabled || isMaxAuthEnabled || showVk) && width < 370" class="text-center my-5">
        {{ $t('modal.auth_modal_state.login.google_sub_text') }}
      </span>

      <UDivider
        v-if="(isTelegramAuthEnabled || isMaxAuthEnabled || showVk) && width >= 370"
        class="modelReg__alternative"
        :label="$t('modal.auth_modal_state.login.google_sub_text')"
        :ui="{ container: { horizontal: 'mx-3 whitespace-nowrap' } }"
      />

      <UForm class="modelReg__form">
        <UiPhoneInputInternational
          v-model="form.phone"
          :data-testid="'e2e-auth-phone-input'"
          :label="$t('modal.auth_modal_state.login.input_phone')"
          :error="v$.phone.$error"
          :errors="v$.phone.$errors"
          :show-error="v$.phone.$dirty"
          @change="v$.phone.$touch"
        />

        <UiKitInput
          v-model="form.password"
          type="password"
          size="lg"
          :placeholder="$t('modal.auth_modal_state.registration.input_password')"
          @blur="v$.password.$touch"
        />

        <!-- Визуальная шкала силы пароля -->
        <PasswordStrengthMeter :password="form.password" />

        <!-- Требования к паролю -->
        <div class="passwordRequirements">
          <div class="flex gap-1 items-center">
            <IconTheCheck v-if="!v$.password.latinLetterPattern.$invalid"></IconTheCheck>
            <IconTheError v-else></IconTheError>
            <span>{{ $t('form.error_for_input.password.latin_letter_pattern') }}</span>
          </div>
          <div class="flex gap-1 items-center">
            <IconTheCheck
              v-if="!v$.password.mustBeNumber.$invalid && !v$.password.leastOneSpecialCharacter.$invalid"
            ></IconTheCheck>
            <IconTheError v-else></IconTheError>
            <span>{{ $t('form.error_for_input.password.symbol_or_number') }}</span>
          </div>
          <div class="flex gap-1 items-center">
            <IconTheCheck v-if="!v$.password.isCapitalLetter.$invalid"></IconTheCheck>
            <IconTheError v-else></IconTheError>
            <span>{{ $t('form.error_for_input.password.is_capital_letter') }}</span>
          </div>
          <div class="flex gap-1 items-center">
            <IconTheCheck v-if="!v$.password.minLength.$invalid"></IconTheCheck>
            <IconTheError v-else></IconTheError>
            <span>{{ $t('form.error_for_input.password.min_length') }}</span>
          </div>
        </div>

        <UiKitInput
          v-model="form.confirmPassword"
          type="password"
          size="lg"
          :placeholder="$t('modal.auth_modal_state.registration.input_confirm_password')"
          :invalid="v$.confirmPassword.$error"
          :error-message="v$.confirmPassword.$errors?.[0]?.$message"
          @blur="v$.confirmPassword.$touch"
        />
        <!-- Согласия — общие на все способы регистрации, поэтому стоят внизу,
             у кнопки. Обе галочки сняты по умолчанию: согласие должно быть активным
             действием, преотмеченная галочка им не является. -->
        <div ref="consentBlock" class="modelReg__consentGroup">
          <div
            class="modelReg__consent"
            :class="{ 'modelReg__consent--error': showConsentError && !isPolicyAccepted }"
          >
            <UiKitCheckbox
              v-model="isPolicyAccepted"
              data-testid="e2e-auth-policy-accept"
              color="primary"
              size="sm"
              class="mt-0.5 shrink-0"
            />
            <span class="text-xs leading-5">
              Соглашаюсь с
              <NuxtLink to="/privacy" target="_blank" class="text-hui-primary underline" @click.stop>
                политикой конфиденциальности
              </NuxtLink>
              и
              <NuxtLink to="/policy" target="_blank" class="text-hui-primary underline" @click.stop>
                пользовательским соглашением
              </NuxtLink>
              .
            </span>
          </div>

          <!-- Реклама — отдельное согласие (ст. 18 ФЗ «О рекламе»): согласие на
               обработку данных выше им не является. По решению владельца
               обязательное наравне с политикой — оговорку см. в useAuthConsentGate. -->
          <div
            class="modelReg__consent"
            :class="{
              'modelReg__consent--error':
                showConsentError && isMarketingRequired && !isMarketingAccepted,
            }"
          >
            <UiKitCheckbox
              v-model="isMarketingAccepted"
              data-testid="e2e-auth-marketing-accept"
              color="primary"
              size="sm"
              class="mt-0.5 shrink-0"
            />
            <span class="text-xs leading-5 text-hui-foreground/70">
              Хочу получать акции, промокоды и новости меню. Уведомления о заказе
              приходят в любом случае.
            </span>
          </div>
        </div>

        <UiButtonMain
          class="btn"
          color="red"
          size="xl"
          :label="$t('modal.auth_modal_state.registration.registration_btn')"
          block
          type="submit"
          :disabled="runtimeConfig.public.playwright ? false : v$.$invalid"
          @click.prevent="submitRegistrationForm"
        ></UiButtonMain>
      </UForm>
    </div>
  </div>
</template>

<script setup>
/* v8 ignore file */
import { consola } from 'consola';
import { useVuelidate } from '@vuelidate/core';
import { onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { modalStateEnum, useModalStore } from '@shared/model';
import { useFormMessages, useGetWidth, useRulesForForm } from '@shared/lib';

import { useAuthStore } from '~/features/auth';
import { useAuthConsentGate } from '~/features/auth/model/useAuthConsentGate';
import { useInstitutionStore } from '@entities/institution';
import { useTelegramAuth } from '~/features/auth/model/useTelegramAuth';
import { useMaxAuth } from '~/features/auth/model/useMaxAuth';
import { useVkAuth } from '~/features/auth/model/useVkAuth';
import { useYandexAuthV2 } from '~/features/auth/model/useYandexAuthV2';
import { useOnboardingTrigger } from '~/features/onboarding/@x/auth';

const props = defineProps({
  userData: {
    type: Object,
    required: true,
    default: () => ({
      phone: '',
      email: '',
      password: '',
      confirmPassword: '',
      twoFA: '',
      codeFromEmail: '',
    }),
  },
});

const emit = defineEmits(['update-user-data']);

const {
  initTelegramAuth,
  openTelegramLink: openTelegramLinkComposable,
  closeTelegramLinkModal,
  cleanup: cleanupTelegram,
  isLoading: telegramLoading,
  errorMessage: telegramErrorMessage,
  isTelegramAuthEnabled,
  showTelegramLinkModal,
} = useTelegramAuth();

const {
  initMaxAuth,
  openMaxLinkManually,
  closeMaxLinkModal,
  cleanup: cleanupMax,
  isLoading: maxLoading,
  errorMessage: maxErrorMessage,
  isMaxAuthEnabled,
  showMaxLinkModal,
} = useMaxAuth();

const {
  renderOneTap: renderVkOneTap,
  loginNative: loginVkNative,
  cleanup: cleanupVk,
  errorMessage: vkErrorMessage,
  isLoading: isVkLoading,
  isVkAuthEnabled,
  isNativePlatform: isVkNative,
} = useVkAuth();
const vkContainer = ref(null);

// Кнопка VK показывается ровно тогда, когда бэк вернул vk_auth_enabled=true для заведения
// (есть активная строка institution_vk с app_id). Без отдельных фиче-флагов.
const showVk = computed(() => isVkAuthEnabled.value);

// Яндекс авторизация теперь инкапсулирована в компоненте YandexAuthButton
const { cleanup: cleanupYandex } = useYandexAuthV2();

const { width } = useGetWidth();
const storeInstitution = useInstitutionStore();
const runtimeConfig = useRuntimeConfig();
const storeModal = useModalStore();
const storeAuth = useAuthStore();
const { startOnboarding } = useOnboardingTrigger();
const { RulesForFormPhone, RulesForFormPassword, RulesForFormConfirmPassword } = useRulesForForm();

const logger = consola.withTag('Registration');

// ✅ Проверка наличия реферального токена
const { hasPendingToken } = useReferralTokenHandler();

// Обработчик авторизации через Telegram
const handleTelegramAuth = async () => {
  await initTelegramAuth();
};

// Обработчик открытия Telegram по ссылке
const handleOpenTelegramLink = () => {
  openTelegramLinkComposable();
};

// Обработчик авторизации через MAX
const handleMaxAuth = async () => {
  await initMaxAuth();
};

// Обработчик открытия MAX по ссылке (Safari-фолбэк)
const handleOpenMaxLink = () => {
  openMaxLinkManually();
};

/**
 * Нативный вход VK. В вебе кнопки нет — там OneTap рисует SDK внутри своего
 * контейнера, и его клик перехватывает прозрачный слой над контейнером.
 */
const handleVkNativeAuth = async () => {
  if (!requireConsent()) return;
  await loginVkNative();
};

// Отслеживаем ошибки Telegram авторизации
watch(telegramErrorMessage, (newError) => {
  if (newError) {
    errorMessage.value = newError;
  }
});

// Отслеживаем ошибки MAX авторизации
watch(maxErrorMessage, (newError) => {
  if (newError) {
    errorMessage.value = newError;
  }
});

// Отслеживаем ошибки VK ID авторизации
watch(vkErrorMessage, (newError) => {
  if (newError) {
    errorMessage.value = newError;
  }
});

// Рендерим VK-виджет и на случай, если доступность подъехала уже после mount
// (институт/конфиг загрузился позже). renderOneTap идемпотентен (гард rendered).
watch(
  showVk,
  async (enabled) => {
    if (!enabled) return;
    await nextTick();
    if (vkContainer.value) renderVkOneTap(vkContainer.value);
  },
  { immediate: true },
);

const form = ref({ ...props.userData });

// Обе галочки и проверка перед любым способом регистрации — в общем композабле:
// в форме входа ровно те же правила, а разъехавшиеся копии этого гейта уже
// приводили к тому, что соцкнопки шли мимо согласия.
const {
  isPolicyAccepted,
  isMarketingAccepted,
  showConsentError,
  isConsentComplete,
  isMarketingRequired,
  consentBlock,
  requireConsent,
} = useAuthConsentGate();

// Используем composable для управления сообщениями (DRY)
const { loader, errorMessage, successMessage, startLoading, stopLoading, handleError } = useFormMessages();

const rules = computed(() => {
  return {
    phone: RulesForFormPhone(),
    password: RulesForFormPassword(),
    confirmPassword: RulesForFormConfirmPassword(form.value.password),
  };
});
const v$ = useVuelidate(rules, form);

onMounted(() => {
  nextTick(() => {
    v$.value.$reset();
    // Диагностика: сразу видно в консоли, почему кнопка VK есть/нет.
    logger.info('[VK] gate', {
      vk_auth_enabled: storeInstitution.institution?.vk_auth_enabled,
      showVk: showVk.value,
      hasContainer: !!vkContainer.value,
    });
    if (showVk.value && vkContainer.value) {
      renderVkOneTap(vkContainer.value);
    }
  });
});

const submitRegistrationForm = async () => {
  // Тот же гейт, что у соцкнопок: подсветит галочку вместо молчаливого отказа.
  if (!runtimeConfig.public.playwright && !requireConsent()) return;
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const params = {
    phone: form.value.phone,
    password: form.value.password,
    institution_id: storeInstitution.institution?.id,
  };

  startLoading();

  try {
    // ✅ RateLimiter, RetryStrategy и OfflineManager работают внутри store
    const result = await storeAuth.register(params);

    setTimeout(async () => {
      // Проверяем, требуется ли подтверждение (бэк вернул requiresVerification: true)
      if (result?.requiresMaxVerification) {
        // Заведение подтверждает телефон через MAX — открываем MAX-модалку с deep-link.
        logger.log('📱 Открываем MaxNumberConfirmation для верификации через MAX');
        storeModal.updateModalData({ maxVerifyLink: result.maxVerifyLink });
        storeModal.setState(modalStateEnum.MaxNumberConfirmation);
      } else if (result?.requiresVerification) {
        // Показываем модальное окно подтверждения через Telegram
        logger.log('📱 Открываем NumberConfirmation для верификации');
        storeModal.setState(modalStateEnum.NumberConfirmation);
      } else {
        // Подтверждение не требуется - токены уже сохранены. Показываем онбординг
        // новичку (благодарность + баллы + предложение сохранить адрес); если уже
        // показывали — просто закрываем модалку.
        logger.log('✅ Регистрация завершена, подтверждение не требуется');
        if (!(await startOnboarding())) {
          storeModal.close();
        }
      }
    }, 1000);
  } catch (error) {
    handleError(error);
  } finally {
    stopLoading();
  }
};

onBeforeUnmount(() => {
  // Очищаем интервалы опроса при размонтировании компонента
  cleanupTelegram();
  cleanupMax();
  cleanupVk();
  cleanupYandex();
});

watch(
  form,
  (newForm) => {
    emit('update-user-data', newForm);
  },
  { deep: true },
);
</script>

<style scoped lang="scss">
.modelReg__consentGroup {
  @apply grid gap-2 mb-4;
}

.modelReg__consent {
  @apply flex items-start gap-2 rounded-lg transition-colors;

  // Подсветка промаха: человек нажал способ регистрации, не поставив галочку.
  // Красим сам блок, а не кнопку — объяснение должно быть там, где действие.
  &--error {
    @apply -mx-2 px-2 py-1.5 bg-hui-danger/10 ring-1 ring-hui-danger/40 text-hui-danger;
  }
}

.modelReg__methods {
  @apply grid gap-0;
}
</style>
